import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Radio } from 'antd';
import { showFeedback } from '../../../store/ui/ui.actions';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  RadioButton,
  Subtitle,
} from '../../shared';
import moment from 'moment';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
  loadSpatStorages,
  saveFarmSeedingForm,
} from '../../../store/farms/farms.actions';
import { IXeroInvoice } from '../../../entities/subscription.entities';
import GrowerInvoiceCreateView from '../../budget/GrowerInvoiceCreateView';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import { numToStr, toMillisecond, toSecond } from '../../../lib/common.helpers';
import { ICustomValue } from '../../../entities/automation.entities';
import { IMusselSeeding } from '../../../entities/growing.entities';
import { IMusselLineResource } from '../../../entities/farms.entities';
import ExpensesForm from '../../grower-views/ExpensesForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import { addUtilData } from '../../../store/utils/utils.actions';
import {
  SelectFarmsByFarmID,
  SelectMusselSpatsByFarmID,
  SelectSeasonsByFarmID,
  SelectSeedingFields,
} from '../../../store/extra/extra.selector';
import {
  extractSeedingForm,
  getSpatSourceLabel,
} from '../../../lib/farm.helpers';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import { StageLabelSpan } from '../../CommonViews';

interface IFormData {
  parent_id?: number;
  season_name: string;
  spat_size: number | null;
  spat_size_max: number | null;
  density: number | null;
  density_unit: 'spat per m' | 'kgs per m';
  planned_date_seed: number;
  planned_date_harvest: number;
  drop: number | null;
  submersion: number | null;
  spacing: number | null;
  floats: number | null;
  comment: string | null;
  budget_unit_amount: number | null;
  budget_unit_price: number | null;
  custom_values?: ICustomValue[];
  spat_amount?: number;
  line_length?: number;
  spat_storage_id?: number;
}

const defaultForm: IFormData = {
  season_name: '',
  spat_size: null,
  spat_size_max: null,
  density: null,
  density_unit: 'spat per m',
  planned_date_seed: moment().toDate().getTime(),
  planned_date_harvest: moment().toDate().getTime(),
  comment: null,
  budget_unit_amount: null,
  budget_unit_price: null,
  drop: null,
  submersion: null,
  spacing: null,
  floats: null,
};

interface ILineItem {
  line_id: number;
  new_spat: 'NEW' | 'EXISTING';
  spat_storage_id?: number;
  spat_source: string;
  spat_amount: number | null;
  line_length: number | null;
  stage?: number;
}

const spatAmountPlaceholder = (id: number | undefined, spats: any) => {
  const spat = spats.find((y: any) => y.id === id);
  return spat ? `Available: ${spat.available_weight}` : '';
};

interface Props {
  visible: boolean;
  lineData?: IMusselLineResource;
  seedData?: IMusselSeeding;
  onClose: (d: any) => void;
  onConfirm: (d: any) => void;
  overConfirm?: (d: any) => Promise<any>;
  className?: string;
  onlyView?: boolean;
  data?: any;
  warningMessage?: string;
}

const MusselGrowerSeedModal = ({
  visible,
  lineData,
  seedData,
  onClose,
  onConfirm,
  overConfirm,
  className,
  onlyView,
  data: paramData,
  warningMessage,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const farms = SelectFarmsByFarmID(lineData?.farm_id);
  const customFields = SelectSeedingFields(lineData?.farm_id);
  const spatStorages = SelectMusselSpatsByFarmID(lineData?.farm_id);
  const seasonData = SelectSeasonsByFarmID(lineData?.farm_id).map(x => ({
    id: x.id.toString(),
    label: x.name,
    value: x.name,
  }));
  const flatLines = farms.flatMap(x => x.lines);
  const linesOption =
    flatLines.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.farm_name} - ${x.line_name}`,
    })) ?? [];
  const isEdit = !!seedData && !!seedData.id;
  const spatOptions = spatStorages.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: getSpatSourceLabel(x.id, spatStorages),
  }));
  const defaultSavedForm = extractSeedingForm(
    farms.find(x => x.id === lineData?.farm_id),
    paramData,
  );

  const [disabled, setDisabled] = useState(false);
  const [seedLines, setSeedLines] = useState<ILineItem[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    ...defaultSavedForm,
    custom_values: paramData?.custom_values ?? undefined,
  });
  const [seedItems, setSeedItems] = useState<IExpenseRequest[]>([
    { name: '', quantity: 1, amount: 0 },
  ]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [seasonType, setSeasonType] = useState<'old' | 'new'>('old');
  const [submittedData, setSubmittedData] = useState<any>();
  const [addSpatSizeRange, setAddSpatSizeRange] = useState(
    (seedData?.spat_size_max !== null &&
      seedData?.spat_size_max !== undefined) ||
      (paramData?.spat_size_max !== null &&
        paramData?.spat_size_max !== undefined),
  );

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message: translate(lang, message),
        duration: -1,
      }),
    );
  const updateForm = (key: keyof IFormData, val: any) => {
    if (key === 'budget_unit_amount' || key === 'budget_unit_price') {
      val = val.length <= 0 ? null : Number(val);
    } else if (key === 'spat_amount' || key === 'line_length') {
      val = val.length <= 0 ? undefined : Number(val);
    } else if (key === 'spat_storage_id') {
      val = val.length <= 0 ? undefined : Number(val);
    } else if (
      [
        'spat_size',
        'spat_size_max',
        'density',
        'drop',
        'submersion',
        'spacing',
        'floats',
      ].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    }
    setFormData(prv => ({ ...prv, [key]: val }));
  };
  const changeIsNewSpat = (i: number, val: string) => {
    let tmp = [...seedLines];
    tmp[i].new_spat = val as any;
    if (val === 'NEW') {
      tmp[i].spat_storage_id = undefined;
    } else {
      tmp[i].spat_source = '';
    }
    setSeedLines(tmp);
  };
  const updateSeedLine = (
    i: number,
    key: 'spat_id' | 'spat_source' | 'spat_amount' | 'line_length' | 'stage',
    val: string,
  ) => {
    if (key === 'spat_id') {
      const batch = spatStorages.find(x => x.id === Number(val));
      setSeedLines(
        seedLines.map((x, j) =>
          j === i
            ? { ...x, spat_storage_id: batch?.id, stage: batch?.stage }
            : x,
        ),
      );
    } else {
      const v =
        key === 'spat_source' ? val : val.length <= 0 ? null : Number(val);
      setSeedLines(seedLines.map((x, j) => (j === i ? { ...x, [key]: v } : x)));
    }
  };
  const handleChangeSeedLine = (v: string[]) => {
    let tmp = seedLines.filter(x => v.includes(x.line_id.toString()));
    for (let i of v) {
      if (tmp.some(x => x.line_id === Number(i))) continue;
      tmp.push({
        line_id: Number(i),
        spat_source: '',
        new_spat: 'EXISTING',
        spat_amount: null,
        line_length: null,
      });
    }
    setSeedLines(tmp);
  };
  const validData = () => {
    if (!isEdit && seedLines.length <= 0) {
      showError('Please select seed lines');
      return null;
    }
    if (!isEdit && seedLines.some(x => !x.spat_source && !x.spat_storage_id)) {
      showError('Empty spat source is not allowed');
      return null;
    }
    if (seedLines.some(x => !x.line_length)) {
      showError('Empty seeded longline length is not allowed');
      return null;
    }
    if (!isEdit && seedLines.some(x => !x.spat_amount)) {
      showError('Empty spat amount is not allowed');
      return null;
    }
    for (let sx of seedLines.filter(x => x.new_spat === 'EXISTING')) {
      const ln = linesOption.find(x => Number(x.id) === sx.line_id);
      if (!ln) continue;

      const sp = spatStorages.find(x => x.id === sx.spat_storage_id);
      if (!sp) {
        showError(
          translate(lang, '_invalid_spat_storage_line', ln.label),
        );
        return null;
      }
      if (!sx.spat_amount) {
        showError(translate(lang, '_invalid_spat_amt_line', ln.label));
        return null;
      }
      if (!isEdit && sx.spat_amount > (sp.available_weight ?? 0)) {
        showError(
          translate(
            lang,
            '_max_weight_line_spat',
            ln.label,
            sp.source,
            sp.available_weight ?? '',
          ),
        );
        return null;
      }
    }
    if (!formData.season_name) {
      showError('Empty season is not allowed');
      return null;
    }
    if (
      !isEdit &&
      seedItems.length <= 0 &&
      !window.confirm(
        translate(lang, '_enter_empty_expenses_spats'),
      )
    ) {
      showError('_empty_spat_items_allowed');
      return null;
    }
    if (seedItems.some(x => !x.name || !x.amount || !x.unit_price)) {
      showError('_input_correct_spat_items');
      return null;
    }
    const expenses = seedItems.map(x => ({
      name: x.name,
      amount: Number(x.amount),
      unit_price: Number(x.unit_price),
      quantity: Number(x.quantity),
    }));
    let result: any = {
      ...formData,
      planned_date_seed: toSecond(formData.planned_date_seed),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
      drop: Number(formData.drop),
      submersion: Number(formData.submersion),
      spacing: Number(formData.spacing),
      floats: Number(formData.floats),
      spat_size: Number(formData.spat_size),
      density: Number(formData.density),
      lines: seedLines.map(x => ({
        line_id: x.line_id,
        spat_storage_id: x.spat_storage_id,
        spat_source: x.spat_source,
        spat_amount: Number(x.spat_amount),
        line_length: Number(x.line_length),
        stage: x.stage,
      })),
      expenses,
    };
    if (isEdit) {
      delete result.spat_source;
    }
    result.custom_values = formData.custom_values?.filter(x => !!x.field_val);

    return result;
  };
  const invoiceDetail = (): IXeroInvoice => {
    let line_items: any = [];
    if (isEdit) {
      if (!lineData || !seedData) {
        window.alert('Unexpected error! Please contact with support team!');
        throw new Error('Unexpected error!');
      }
      for (let item of seedItems) {
        const quantity = Number(item.quantity) ?? 1;
        line_items.push({
          item_name: `${lineData.farm_name} - ${lineData.line_name}`,
          description: item.name,
          quantity,
          unit_price: Number(item.unit_price),
        });
      }
    } else {
      const totCnt = seedLines.length;
      for (let lID of seedLines) {
        const line = linesOption.find(t => Number(t.id) === lID.line_id);
        if (!line) continue;
        for (let item of seedItems) {
          const quantity =
            item.quantity && Number(item.quantity) ? Number(item.quantity) : 1;
          line_items.push({
            item_name: line.label,
            description: item.name,
            quantity: quantity / totCnt,
            unit_price: Number(item.amount) / quantity,
          });
        }
      }
    }
    return {
      reference: 'Seed Invoice',
      contact_id: '',
      line_items,
      type: 'ACCPAY',
    };
  };
  const closeInvoice = () => {
    const tmp = submittedData;
    setSubmittedData(undefined);
    setShowConfirm(false);
    onConfirm(tmp);
  };
  const confirmClick = async () => {
    const data = validData();
    if (!data) return;

    if (overConfirm) {
      setDisabled(true);
      if (!overConfirm(data)) {
        setDisabled(false);
      }
      return;
    }

    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }

    setDisabled(true);
    if (!seasonData.some(x => x.value === data.season_name)) {
      dispatch(addUtilData('season', data.season_name));
    }
    const res = isEdit
      ? await sendSingleRequest(
          data,
          'PUT',
          `api/farm/line/seeding/${seedData?.id}`,
          true,
        )
      : await sendSingleRequest(
          data,
          'POST',
          'api/farm/line/multiline-seeding',
          true,
        );
    setDisabled(false);
    if (res.status) {
      dispatch(
        showFeedback({
          isMessageModal: true,
          message: translate(
            lang,
            res.data?.message
              ? res.data.message
              : isEdit
                ? 'Successfully updated'
                : 'Successfully seeded',
          ),
          type: 'success',
        }),
      );
      setSubmittedData(res.data);
      if (lineData) {
        saveFarmSeedingForm(dispatch, lineData?.farm_id, {
          season_name: data.season_name,
          spacing: data.spacing,
        });
      }
      dispatch(loadFarmsData());
      dispatch(loadSpatStorages());
      dispatch(loadQueueData());
    } else {
      showError(translate(lang, res.data?.message ?? 'Failed to seed'));
    }
  };

  useEffect(() => {
    let tmpSeedLines = [...seedLines];
    if (lineData) {
      if (!tmpSeedLines.some(x => x.line_id === lineData.id)) {
        tmpSeedLines.push({
          line_id: lineData.id,
          line_length: null,
          new_spat: 'EXISTING',
          spat_amount: null,
          spat_source: '',
        });
      }
    }
    if (seedData && seedData.id) {
      if (seedData.expense_items) {
        const items = seedData.expense_items.map(x => ({
          name: x.name,
          amount: x.amount,
          unit_price: Math.round((x.amount / (x.quantity ?? 1)) * 100) / 100,
          quantity: x.quantity ?? 0,
        }));
        setSeedItems(items);
      }
      let tmp = { ...formData };
      for (let k of Object.keys(formData)) {
        let val = (seedData as any)[k];
        if (k === 'planned_date_seed' || k === 'planned_date_harvest') {
          val = val * 1000;
        }
        if ((seedData as any)[k] !== undefined) (tmp as any)[k] = val;
      }
      const i = tmpSeedLines.findIndex(x => x.line_id === lineData?.id);
      if (i > -1) {
        tmpSeedLines[i].new_spat = seedData.spat_storage ? 'EXISTING' : 'NEW';
        tmpSeedLines[i].spat_storage_id = seedData.spat_storage?.id;
        tmpSeedLines[i].spat_source = seedData.spat_storage?.source ?? '';
        tmpSeedLines[i].line_length = seedData.line_length;
        tmpSeedLines[i].spat_amount = seedData.spat_amount;
        tmpSeedLines[i].stage = seedData.stage;
      }
      if (seedData.spat_storage) {
        tmp.spat_storage_id = seedData.spat_storage.id;
      }
      if (seedData.spat_amount) {
        tmp.spat_amount = seedData.spat_amount;
      }
      if (seedData.line_length) {
        tmp.line_length = seedData.line_length;
      }
      setFormData(tmp);
    }
    setSeedLines(tmpSeedLines);
  }, [lineData, seedData]);

  useEffect(() => {
    if (paramData) {
      let tmpLines: ILineItem[] = [];
      const {
        lines,
        expenses,
        planned_date_seed,
        planned_date_harvest,
        line_length,
        spat_amount,
        spat_storage_id,
        parent_id,
        ...params
      } = paramData;
      if (lines && lines.length > 0) {
        for (const ln of lines) {
          const spat = spatStorages.find(x => x.id === ln.spat_storage_id);
          tmpLines.push({
            line_id: ln.line_id,
            new_spat: spat ? 'EXISTING' : 'NEW',
            spat_storage_id: spat?.id,
            spat_source: ln.spat_source,
            spat_amount: ln.spat_amount,
            line_length: ln.line_length,
            stage: ln.stage ?? spat?.stage,
          });
        }
        setSeedLines(tmpLines);
      } else if (lineData) {
        setSeedLines([
          {
            line_id: lineData.id,
            line_length: line_length ?? null,
            spat_amount: spat_amount ?? null,
            new_spat: spat_storage_id ? 'EXISTING' : 'NEW',
            spat_storage_id: spat_storage_id ?? undefined,
            spat_source: '',
          },
        ]);
      }
      if (expenses) {
        setSeedItems(expenses);
      }
      setFormData({
        ...params,
        planned_date_seed: toMillisecond(planned_date_seed),
        planned_date_harvest: toMillisecond(planned_date_harvest),
      });
    }
  }, [paramData, lineData]);

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className={className}
      closable
      maskClosable={false}
      closeIcon={<CloseIcon />}
      width={900}
      footer={null}
    >
      {!showConfirm ? (
        <div className='section-modal wrap'>
          <div className='d-flex align-center mb-17'>
            <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
              {translate(lang, 'INVOICE DETAILS')}
            </Subtitle>
          </div>
          {!!warningMessage && (
            <div className='header-alert'>{warningMessage}</div>
          )}
          {!isEdit ? (
            <div className='section'>
              <div className='mb-17'>
                <Dropdown
                  mode='multiple'
                  label={translate(lang, 'Select seeded lines')}
                  placeholder={translate(lang, 'Select seeded lines')}
                  options={linesOption}
                  value={seedLines.map(x => x.line_id.toString()) as any}
                  onChange={(v: any) => handleChangeSeedLine(v)}
                  showSearch={true}
                />
              </div>
              {seedLines.map((line, i) => (
                <div key={i}>
                  <div className='item-row'>
                    <Subtitle
                      color='black-2'
                      align='left'
                      size={5}
                      fontWeight={500}
                    >
                      {translate(
                        lang,
                        '_spat_longline_data',
                        linesOption.find(y => Number(y.value) === line.line_id)
                          ?.label ?? '',
                      )}
                    </Subtitle>
                  </div>
                  <div className='item-row'>
                    <div className='item-cell'>
                      <Dropdown
                        label={translate(lang, 'Is new spat?')}
                        options={[
                          {
                            id: 'N',
                            value: 'NEW',
                            label: translate(lang, 'New'),
                          },
                          {
                            id: 'E',
                            value: 'EXISTING',
                            label: translate(lang, 'Existing'),
                          },
                        ]}
                        value={line.new_spat}
                        onChange={v => changeIsNewSpat(i, v)}
                      />
                    </div>
                    <div className='item-cell'>
                      {line.new_spat === 'NEW' ? (
                        <Input
                          type='text'
                          onChange={e =>
                            updateSeedLine(i, 'spat_source', e.target.value)
                          }
                          value={line.spat_source}
                          label={translate(lang, 'Spat Source')}
                          disabled={isEdit}
                        />
                      ) : (
                        <Dropdown
                          label={translate(lang, 'Select spat source')}
                          options={spatOptions}
                          value={line.spat_storage_id?.toString()}
                          onChange={v => updateSeedLine(i, 'spat_id', v)}
                        />
                      )}
                    </div>
                  </div>
                  <div className='item-row mb-17'>
                    <div className='item-cell'>
                      <Input
                        type='number'
                        label={translate(lang, 'Spat amount')}
                        placeholder={spatAmountPlaceholder(
                          line.spat_storage_id,
                          spatStorages,
                        )}
                        onChange={e =>
                          updateSeedLine(i, 'spat_amount', e.target.value)
                        }
                        value={numToStr(line.spat_amount)}
                        unit='kg'
                        disabled={isEdit}
                      />
                    </div>
                    <div className='item-cell'>
                      <Input
                        type='number'
                        label={translate(lang, 'Longline length')}
                        value={numToStr(line.line_length)}
                        onChange={e =>
                          updateSeedLine(i, 'line_length', e.target.value)
                        }
                        unit='m'
                        disabled={isEdit}
                      />
                    </div>
                    <div className='item-cell'>
                      <Dropdown
                        label={translate(lang, 'Batch Stage')}
                        options={[1, 2, 3, 4].map(x => ({
                          id: x.toString(),
                          value: x.toString(),
                          label: (
                            <StageLabelSpan
                              stage={x}
                              farmId={lineData?.farm_id}
                            />
                          ),
                        }))}
                        value={line.stage?.toString()}
                        onChange={v => updateSeedLine(i, 'stage', v)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='section'>
              <div className='item-row mb-17'>
                <div className='item-cell'>
                  <Dropdown
                    label={translate(lang, 'Select spat source')}
                    options={spatOptions}
                    value={formData.spat_storage_id?.toString()}
                    onChange={v => updateForm('spat_storage_id', v)}
                  />
                </div>
                <div className='item-cell'>
                  <Input
                    type='number'
                    label={translate(lang, 'Spat amount')}
                    placeholder={spatAmountPlaceholder(
                      formData.spat_storage_id,
                      spatStorages,
                    )}
                    onChange={e => updateForm('spat_amount', e.target.value)}
                    value={numToStr(formData.spat_amount)}
                    unit='kg'
                  />
                </div>
                <div className='item-cell'>
                  <Input
                    type='number'
                    label={translate(lang, 'Longline length')}
                    value={numToStr(formData.line_length)}
                    onChange={e => updateForm('line_length', e.target.value)}
                    unit='m'
                  />
                </div>
              </div>
            </div>
          )}
          <div className='section'>
            <div className='item-row mb-17 align-end'>
              <div className='item-cell'>
                <Radio.Group
                  className='d-flex mb-16'
                  onChange={e => setSeasonType(e.target.value)}
                  value={seasonType}
                >
                  <RadioButton
                    label={translate(lang, 'Existing season')}
                    value='old'
                  />
                  <RadioButton
                    className='ml-34'
                    label={translate(lang, 'New season')}
                    value='new'
                  />
                </Radio.Group>
                <div>
                  {seasonType === 'old' ? (
                    <Dropdown
                      label={translate(lang, 'Season name')}
                      placeholder={translate(lang, 'Choose Season')}
                      className='mr-16 w-100'
                      onChange={value => updateForm('season_name', value)}
                      options={seasonData}
                      value={formData.season_name}
                    />
                  ) : (
                    <Input
                      label={translate(lang, 'Season name')}
                      type='text'
                      onChange={e => updateForm('season_name', e.target.value)}
                      value={formData.season_name}
                    />
                  )}
                </div>
              </div>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Floats')}
                  type='number'
                  onChange={e => updateForm('floats', e.target.value)}
                  value={numToStr(formData.floats)}
                />
              </div>
            </div>
            <div className='item-row'>
              <CheckboxButton
                label={translate(lang, 'Add spat size range')}
                checked={addSpatSizeRange}
                onChange={e => {
                  setAddSpatSizeRange(e.target.checked);
                  if (!e.target.checked) {
                    updateForm('spat_size_max', '');
                  }
                }}
                disabled={onlyView}
              />
            </div>
            <div className='item-row mb-17'>
              {addSpatSizeRange ? (
                <>
                  <div className='item-cell'>
                    <Input
                      label={translate(lang, 'Minimum spat size')}
                      type='number'
                      onChange={e => updateForm('spat_size', e.target.value)}
                      value={numToStr(formData.spat_size)}
                      unit='mm'
                    />
                  </div>
                  <div className='item-cell'>
                    <Input
                      label={translate(lang, 'Maximum spat size')}
                      type='number'
                      onChange={e =>
                        updateForm('spat_size_max', e.target.value)
                      }
                      value={numToStr(formData.spat_size_max)}
                      unit='mm'
                    />
                  </div>
                </>
              ) : (
                <div className='item-cell'>
                  <Input
                    label={translate(lang, 'Spat size')}
                    type='number'
                    onChange={e => updateForm('spat_size', e.target.value)}
                    value={numToStr(formData.spat_size)}
                    unit='mm'
                  />
                </div>
              )}
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Drop')}
                  type='number'
                  onChange={e => updateForm('drop', e.target.value)}
                  value={numToStr(formData.drop)}
                  unit='m'
                />
              </div>
            </div>
            <div className='item-row mb-17'>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Submersion')}
                  type='number'
                  onChange={e => updateForm('submersion', e.target.value)}
                  value={numToStr(formData.submersion)}
                  unit='m'
                />
              </div>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Spacing')}
                  type='number'
                  onChange={e => updateForm('spacing', e.target.value)}
                  value={numToStr(formData.spacing)}
                  unit='mm'
                />
              </div>
              <div className='item-cell'>
                <Input
                  label={translate(lang, 'Density')}
                  type='number'
                  onChange={e => updateForm('density', e.target.value)}
                  value={numToStr(formData.density)}
                  unit={formData.density_unit}
                />
              </div>
            </div>
            <div className='item-row mb-17'>
              <div className='item-cell'>
                <Datepicker
                  label={translate(lang, 'Date seed')}
                  defaultValue={formData.planned_date_seed}
                  onChange={e =>
                    e && updateForm('planned_date_seed', e.toDate().getTime())
                  }
                />
              </div>
              <div className='item-cell'>
                <Datepicker
                  label={translate(lang, 'Planned harvest date')}
                  defaultValue={formData.planned_date_harvest}
                  onChange={e =>
                    e &&
                    updateForm('planned_date_harvest', e.toDate().getTime())
                  }
                />
              </div>
            </div>
            <div className='item-row mb-17'>
              <div className='item-cell'>
                <Input
                  type='number'
                  label={translate(lang, 'Budgeted kg/m')}
                  unit='kg/m'
                  onChange={e =>
                    updateForm('budget_unit_amount', e.target.value)
                  }
                  value={numToStr(formData.budget_unit_amount)}
                />
              </div>
              <div className='item-cell'>
                <Input
                  type='number'
                  label={translate(lang, 'Budgeted $/m')}
                  unit='$/m'
                  onChange={e =>
                    updateForm('budget_unit_price', e.target.value)
                  }
                  value={numToStr(formData.budget_unit_price)}
                />
              </div>
            </div>
            <CustomValuesForm
              fields={customFields}
              values={formData.custom_values ?? []}
              onChange={d => setFormData(prv => ({ ...prv, custom_values: d }))}
            />
          </div>
          <ExpensesForm
            title={translate(lang, 'Seed items')}
            className='section'
            data={seedItems}
            setData={setSeedItems}
          />
          <div className='section'>
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Comment')}
                placeholder={translate(lang, '_write_desc_here')}
                type='textarea'
                onChange={e => updateForm('comment', e.target.value)}
                value={formData.comment ?? ''}
                name='comment'
              />
            </div>
          </div>
          <div className='modal-button d-flex justify-end align-center'>
            <Button
              width='small'
              size={2}
              type='fill'
              color='red'
              className='rsp-btn'
              onClick={onClose}
              disabled={disabled}
            >
              {translate(lang, 'Cancel')}
            </Button>
            <Button
              width='small'
              size={2}
              type='fill'
              color='green'
              className='rsp-btn ml-16'
              onClick={confirmClick}
              disabled={disabled || onlyView}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='section-modal wrap'>
          <GrowerInvoiceCreateView
            farmID={lineData?.farm_id}
            invoice={invoiceDetail()}
            onClose={closeInvoice}
            showButton={!!submittedData}
          />
          {!submittedData && (
            <div className='modal-button d-flex justify-end align-center'>
              <Button
                width='small'
                className='rsp-btn'
                size={2}
                type='fill'
                color='red'
                onClick={() => setShowConfirm(false)}
                disabled={disabled}
              >
                {translate(lang, 'Go Back')}
              </Button>
              <Button
                width='small'
                size={2}
                type='fill'
                color='green'
                className='rsp-btn ml-16'
                onClick={confirmClick}
                disabled={disabled}
              >
                {translate(lang, 'Confirm')}
              </Button>
            </div>
          )}
        </div>
      )}
      <ModalFeedbackView />
    </Modal>
  );
};

export default MusselGrowerSeedModal;
